import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";

import "./index.css";
import { ApiError } from "~/api";
import { BootstrapProvider } from "~/bootstrap";
import { Layout } from "~/layout";
import { AppTheme } from "~/theme";
import { SignInPage } from "~/features/signin";
import { PasswordPage } from "~/features/password";
import { HomePage } from "~/features/home";
import { PickPage } from "~/features/plan";
import { SettingsPage } from "~/features/settings";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Losing the session is an ordinary outcome, not something to retry.
      retry: (count, error) =>
        !(error instanceof ApiError && error.isUnauthenticated) && count < 2,
      // The administration interface turns this off; the portal wants it,
      // because somebody coming back to a tab wants to see what happened to
      // the absence they planned.
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
});

/** ToSignIn sends the browser to the sign-in screen from inside the router. */
function ToSignIn() {
  const navigate = useNavigate();
  queueMicrotask(() => navigate("/login", { replace: true }));
  return null;
}

/** The legacy loading state: a large thin spinner on a dimmed page. */
function Loading() {
  return (
    <Backdrop open invisible>
      <CircularProgress size={108} thickness={2} />
    </Backdrop>
  );
}

// The routes are the legacy ones under the portal's names: /dashboard is "/",
// /select is "/neu" (the phone's wizard) and /settings is "/einstellungen".
// Planning, editing and favourites are dialogs over the start page, as they
// were; the old portal's pages for them lead back to it.
function App() {
  return (
    <Routes>
      <Route path="/login" element={<SignInPage />} />
      <Route path="/login/passwort" element={<PasswordPage />} />
      <Route
        element={
          <BootstrapProvider fallback={<Loading />} signedOut={<ToSignIn />}>
            <Layout />
          </BootstrapProvider>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="/neu" element={<PickPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppTheme>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppTheme>
    </QueryClientProvider>
  </StrictMode>,
);
